import { Copy } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { ProcessingPipelineState } from '@/services/api/adminProcessing'
import { FailuresMobileList } from './FailuresMobileList'

export function FailuresCard({
  pipeline,
  rangeLabel,
}: {
  pipeline: ProcessingPipelineState
  rangeLabel: string
}) {
  const { t } = useTranslation()
  const copyError = (error: string) => {
    navigator.clipboard.writeText(error)
    toast.success(t('admin.processing.failures.copied'))
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t('admin.processing.failures.title')} · {rangeLabel} ({pipeline.failures.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-2">
        {pipeline.failures.length === 0 ? (
          <div className="px-2 py-2 text-sm text-muted-foreground">
            {t('admin.processing.failures.empty')}
          </div>
        ) : (
          <Table className="max-md:hidden">
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.processing.failures.colTime')}</TableHead>
                <TableHead>{t('admin.processing.cols.job')}</TableHead>
                <TableHead>{t('admin.processing.cols.step')}</TableHead>
                <TableHead className="w-full">{t('admin.processing.failures.colError')}</TableHead>
                <TableHead className="w-[40px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pipeline.failures.map((f, i) => (
                <TableRow key={`${f.job_id}-${f.step_name}-${i}`}>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {f.ended_at ? new Date(f.ended_at).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{f.job_id.slice(0, 8)}</TableCell>
                  <TableCell className="font-mono text-xs">{f.step_name ?? '—'}</TableCell>
                  <TableCell className="w-full max-w-0 text-xs text-destructive">
                    <TooltipProvider delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="min-w-0 cursor-default truncate">{f.error}</div>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          align="start"
                          className="max-w-[720px] whitespace-pre-wrap break-all"
                        >
                          {f.error}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => copyError(f.error)}
                      aria-label={t('admin.processing.failures.copyError')}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <FailuresMobileList failures={pipeline.failures} onCopy={copyError} />
      </CardContent>
    </Card>
  )
}
